import { parseChord, tableName } from "./NoteBar/voicings";

/**
 * 연주 체크 (2026-10, 새 디자인). 마이크 소리를 12음(도~시) 세기로 나눠(크로마),
 * 지금 악보 코드의 구성음에 소리가 얼마나 몰려 있는지로 맞게 치는지 판단한다.
 * 정확한 채점이 아니라 '대체로 맞게 치고 있나'를 알려 주는 연습용.
 */
let stream: MediaStream | null = null;
let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let freq: Float32Array | null = null;
let wave: Float32Array | null = null;
let proc: ScriptProcessorNode | null = null;

// 박자 채점용: 기타를 치는 순간(소리가 갑자기 커지는 때)의 시각(performance.now 기준 ms)
let onsets: number[] = [];
export const takeOnsets = () => {
  const o = onsets;
  onsets = [];
  return o;
};

export const startListening = async () => {
  if (analyser) return;
  stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } as any,
  });
  const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
  ctx = new Ctx() as AudioContext;
  if (ctx.state === "suspended") await ctx.resume();
  const src = ctx.createMediaStreamSource(stream);
  analyser = ctx.createAnalyser();
  analyser.fftSize = 8192;
  analyser.smoothingTimeConstant = 0.5;
  src.connect(analyser);
  // 약 10ms 조각마다 소리 크기를 보고, 직전보다 확 커지면 '친 순간'으로 기록 (0.12초 안에 두 번은 무시)
  const hist: number[] = [];
  let last = 0;
  proc = ctx.createScriptProcessor(512, 1, 1);
  proc.onaudioprocess = (e) => {
    const d = e.inputBuffer.getChannelData(0);
    let sum = 0;
    for (let i = 0; i < d.length; i++) sum += d[i] * d[i];
    const rms = Math.sqrt(sum / d.length);
    const avg = hist.length ? hist.reduce((a, b) => a + b, 0) / hist.length : rms;
    const now = performance.now();
    if (rms > 0.02 && rms > avg * 1.8 && now - last > 120) {
      onsets.push(now);
      last = now;
    }
    hist.push(rms);
    if (hist.length > 8) hist.shift();
  };
  src.connect(proc);
  const mute = ctx.createGain();
  mute.gain.value = 0;
  proc.connect(mute).connect(ctx.destination);
  freq = new Float32Array(analyser.frequencyBinCount);
  wave = new Float32Array(analyser.fftSize);
};

export const stopListening = () => {
  if (proc) proc.onaudioprocess = null;
  proc = null;
  stream?.getTracks().forEach((t) => t.stop());
  ctx?.close().catch(() => {});
  stream = null;
  ctx = null;
  analyser = null;
};

// 코드 이름(표시 HTML) → 구성음 (0=도 … 11=시)
const pcsCache = new Map<string, number[]>();
export const chordPcs = (html: string): number[] => {
  if (pcsCache.has(html)) return pcsCache.get(html)!;
  const p = parseChord(tableName(html.replace(/<sup>#<\/sup>/g, "#").replace(/<[^>]+>/g, "")));
  const pcs = p ? Array.from(new Set([...Array.from(p.pcs), p.bass])) : [];
  pcsCache.set(html, pcs);
  return pcs;
};

/** 지금 소리 한 조각의 12음 세기(합 1). null = 소리가 작음(안 치는 중) */
export const sample = (): number[] | null => {
  if (!analyser || !ctx || !freq || !wave) return null;
  analyser.getFloatTimeDomainData(wave as any);
  let rms = 0;
  for (let i = 0; i < wave.length; i++) rms += wave[i] * wave[i];
  rms = Math.sqrt(rms / wave.length);
  if (rms < 0.008) return null;
  analyser.getFloatFrequencyData(freq as any);
  const binHz = ctx.sampleRate / analyser.fftSize;
  const chroma = new Array(12).fill(0);
  const lo = Math.floor(70 / binHz);
  const hi = Math.min(freq.length - 1, Math.ceil(1500 / binHz));
  for (let k = lo; k <= hi; k++) {
    const mag = Math.pow(10, freq[k] / 20);
    const midi = 12 * Math.log2((k * binHz) / 440) + 69;
    const near = Math.round(midi);
    if (Math.abs(midi - near) > 0.35) continue; // 음과 음 사이 잡음은 빼기
    chroma[((near % 12) + 12) % 12] += mag * mag;
  }
  const total = chroma.reduce((a, b) => a + b, 0);
  return total ? chroma.map((v) => v / total) : null;
};

const fit = (chroma: number[], pcs: number[]) => pcs.reduce((a, pc) => a + chroma[pc], 0) - pcs.length / 12;

/**
 * 맞게 치는 중인가: 지금 코드가 이 곡의 다른 코드들보다 소리와 잘 맞으면(거의 1등) 맞음.
 * (C 와 Am 처럼 음을 나눠 쓰는 코드끼리도 구별하려고 '비율'이 아니라 '곡 안의 순위'로 본다)
 */
export const judge = (chroma: number[], pcs: number[], songPcs: number[][]): boolean => {
  if (!pcs.length) return true;
  const mine = fit(chroma, pcs);
  const best = Math.max(mine, ...songPcs.map((p) => fit(chroma, p)));
  return mine >= best - 0.05 && pcs.reduce((a, pc) => a + chroma[pc], 0) >= 0.45;
};

/**
 * 박자 점수(0~100). beats = 재생 중 박이 바뀐 시각들, hits = 친 순간들.
 * 친 순간이 반 박 격자에서 얼마나 벗어났는지 보되, 마이크가 늦게 듣는 시간(사람마다 일정)은 빼고 본다.
 * 친 순간이 너무 적으면(8번 미만) null.
 */
export const rhythmScore = (beats: number[], hits: number[]): number | null => {
  if (beats.length < 4) return null;
  const errs: number[] = [];
  hits.forEach((h) => {
    let i = -1;
    for (let k = 0; k < beats.length - 1; k++) if (beats[k] <= h && h < beats[k + 1]) i = k;
    if (i < 0) return;
    const half = (beats[i + 1] - beats[i]) / 2;
    if (half < 80) return;
    let e = (h - beats[i]) % half;
    if (e > half / 2) e -= half;
    errs.push(e / half); // -0.5 ~ 0.5
  });
  if (errs.length < 8) return null;
  const sorted = [...errs].sort((a, b) => a - b);
  const lag = sorted[Math.floor(sorted.length / 2)];
  const dev = errs.reduce((a, e) => {
    let d = Math.abs(e - lag);
    if (d > 0.5) d = 1 - d;
    return a + d;
  }, 0) / errs.length;
  return Math.round(Math.max(0, 1 - dev / 0.25) * 100);
};
